import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
`;

const Title = styled.strong`
  font-size: 0.9rem;
`;

const HeaderButton = styled.button`
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.35rem 0.6rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const FilterRow = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
`;

const FilterHeader = styled.button`
  width: 100%;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.55rem;
  cursor: pointer;
`;

const FilterHeaderLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const FilterHeaderRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
`;

const FilterLabel = styled.label`
  font-size: 0.85rem;
`;

const FilterBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})`
  border: 1px solid ${({ theme, active }) => active ? theme.colors.success : theme.colors.borderDefault};
  background: ${({ theme, active }) => active ? theme.colors.success : theme.colors.transparent};
  color: ${({ theme, active }) => active ? theme.colors.onSuccess : theme.colors.txtSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.15rem 0.3rem;
  line-height: 1;
`;

const FilterBody = styled.div`
  padding: 0.45rem;
`;

const FilterBodyTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
`;

const FilterActions = styled.div`
  display: inline-flex;
  gap: 0.25rem;
`;

const MiniButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.12rem 0.35rem;
  font-size: 0.72rem;
  cursor: pointer;
`;

const FilterOptions = styled.div`
  max-height: 190px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.2rem;
`;

const FilterOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
`;

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
};

const areStringArraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (String(a[i]) !== String(b[i])) return false;
  }
  return true;
};

const defaultGetValue = (row, column) => {
  const raw = column?.getValue ? column.getValue(row) : row?.[column?.key];
  if (raw === null || raw === undefined) return '';
  return raw;
};

const normalizeRowValues = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue.map((value) => String(value).trim()).filter(Boolean);
  }
  const value = String(rawValue).trim();
  return value ? [value] : [];
};

const EMPTY_OBJECT = Object.freeze({});

const DynamicFilterBuilder = ({
  title = 'Filtros dinamicos',
  rows = [],
  columns = [],
  value,
  onChange,
}) => {
  const columnFilters = value?.columnFilters || EMPTY_OBJECT;
  const touchedFilters = value?.touchedFilters || EMPTY_OBJECT;
  const expandedFilterGroups = value?.expandedFilterGroups || EMPTY_OBJECT;

  const getColumnValue = defaultGetValue;

  const applyColumnFilters = useMemo(() => {
    return (dataset, excludeColumnKey = null) => dataset.filter((row) => {
      for (const [columnKey, isTouched] of Object.entries(touchedFilters)) {
        if (!isTouched) continue;
        if (excludeColumnKey && columnKey === excludeColumnKey) continue;

        const col = columns.find((item) => item.key === columnKey);
        if (!col) continue;

        const selectedValues = ensureArray(columnFilters[columnKey]);
        const valueText = normalizeRowValues(getColumnValue(row, col));

        if (selectedValues.length === 0) return false;
        if (!valueText.some((value) => selectedValues.includes(value))) return false;
      }
      return true;
    });
  }, [columnFilters, columns, getColumnValue, touchedFilters]);

  const optionsByColumn = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      const constrainedRows = applyColumnFilters(rows, col.key);
      const values = new Set();
      constrainedRows.forEach((row) => {
        normalizeRowValues(getColumnValue(row, col)).forEach((valueText) => {
          if (valueText) values.add(valueText);
        });
      });
      map[col.key] = Array.from(values).sort((a, b) => a.localeCompare(b));
    });
    return map;
  }, [applyColumnFilters, columns, getColumnValue, rows]);

  useEffect(() => {
    let changed = false;
    const nextFilters = { ...columnFilters };
    const nextTouched = { ...touchedFilters };

    columns.forEach((col) => {
      if (!touchedFilters[col.key]) return;
      const options = optionsByColumn[col.key] || [];
      const current = ensureArray(columnFilters[col.key]);
      const pruned = current.filter((v) => options.includes(v));

      if (!areStringArraysEqual(current, pruned)) {
        nextFilters[col.key] = pruned;
        changed = true;
      }

      if (pruned.length === options.length) {
        delete nextTouched[col.key];
        delete nextFilters[col.key];
        changed = true;
      }
    });

    if (changed && onChange) {
      onChange({
        columnFilters: nextFilters,
        touchedFilters: nextTouched,
        expandedFilterGroups,
      });
    }
  }, [columnFilters, columns, expandedFilterGroups, onChange, optionsByColumn, touchedFilters]);

  const getEffectiveSelectedValues = (columnKey, options) => {
    if (touchedFilters[columnKey]) {
      return ensureArray(columnFilters[columnKey]);
    }
    return options;
  };

  const updateState = (nextPartial) => {
    if (!onChange) return;
    onChange({
      columnFilters,
      touchedFilters,
      expandedFilterGroups,
      ...nextPartial,
    });
  };

  const clearFilters = () => {
    updateState({ columnFilters: {}, touchedFilters: {} });
  };

  const toggleFilterGroup = (columnKey) => {
    updateState({
      expandedFilterGroups: {
        ...expandedFilterGroups,
        [columnKey]: !expandedFilterGroups[columnKey],
      },
    });
  };

  const toggleFilterOption = (columnKey, option) => {
    const options = optionsByColumn[columnKey] || [];
    const currentValues = touchedFilters[columnKey]
      ? ensureArray(columnFilters[columnKey])
      : options;
    const exists = currentValues.includes(option);

    updateState({
      columnFilters: {
        ...columnFilters,
        [columnKey]: exists ? currentValues.filter((v) => v !== option) : [...currentValues, option],
      },
      touchedFilters: {
        ...touchedFilters,
        [columnKey]: true,
      },
    });
  };

  const selectAllForColumn = (columnKey) => {
    const nextFilters = { ...columnFilters };
    delete nextFilters[columnKey];

    const nextTouched = { ...touchedFilters };
    delete nextTouched[columnKey];

    updateState({
      columnFilters: nextFilters,
      touchedFilters: nextTouched,
    });
  };

  const clearColumnFilter = (columnKey) => {
    updateState({
      columnFilters: {
        ...columnFilters,
        [columnKey]: [],
      },
      touchedFilters: {
        ...touchedFilters,
        [columnKey]: true,
      },
    });
  };

  return (
    <Wrapper>
      <TopBar>
        <Title>{title}</Title>
        <HeaderButton type="button" onClick={clearFilters}>Limpiar</HeaderButton>
      </TopBar>

      {columns.map((col) => {
        const options = optionsByColumn[col.key] || [];
        const selectedValues = getEffectiveSelectedValues(col.key, options);
        const totalCount = options.length;
        const selectedCount = selectedValues.length;
        const isFiltered = !!touchedFilters[col.key] && (selectedCount !== totalCount);
        const isExpanded = expandedFilterGroups[col.key] ?? false;

        return (
          <FilterRow key={col.key}>
            <FilterHeader type="button" onClick={() => toggleFilterGroup(col.key)}>
              <FilterHeaderLeft>
                <i className="bi bi-funnel"></i>
                <FilterLabel>{col.label}</FilterLabel>
              </FilterHeaderLeft>
              <FilterHeaderRight>
                <FilterBadge active={isFiltered}>{isFiltered ? 'Filtrado' : 'Sin filtro'}</FilterBadge>
                <span>{selectedCount}/{totalCount}</span>
                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </FilterHeaderRight>
            </FilterHeader>

            {isExpanded && (
              <FilterBody>
                <FilterBodyTop>
                  <small>Seleccion multiple</small>
                  <FilterActions>
                    <MiniButton type="button" onClick={() => selectAllForColumn(col.key)}>Todo</MiniButton>
                    <MiniButton type="button" onClick={() => clearColumnFilter(col.key)}>Ninguno</MiniButton>
                  </FilterActions>
                </FilterBodyTop>

                <FilterOptions>
                  {options.map((option) => (
                    <FilterOption key={`${col.key}-${option}`}>
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(option)}
                        onChange={() => toggleFilterOption(col.key, option)}
                      />
                      {option}
                    </FilterOption>
                  ))}
                </FilterOptions>
              </FilterBody>
            )}
          </FilterRow>
        );
      })}
    </Wrapper>
  );
};

export default DynamicFilterBuilder;
